import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import {
  FileText, Upload, Eye, Trash2, Sparkles, Loader2, CheckCircle2, Clock, FolderOpen, Plus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { loanProfilingApi } from '@/services/api';

const GROUP_LABELS = {
  KYC: 'KYC', INCOME: 'Income', GUARANTOR: 'Guarantor / Co-applicant',
  PROPERTY: 'Property', VEHICLE: 'Vehicle', EXISTING_LOAN: 'Existing Loan', OTHER: 'Other',
};
const GROUP_ORDER = ['KYC', 'INCOME', 'PROPERTY', 'GUARANTOR', 'VEHICLE', 'EXISTING_LOAN', 'OTHER'];

const STATUS_STYLE = {
  VERIFIED: 'text-emerald-700 bg-emerald-50',
  REVIEW: 'text-amber-700 bg-amber-50',
  PENDING: 'text-slate-500 bg-slate-100',
};

const STATEMENT_TYPES = ['bank_statement', 'guarantor_bank_statement', 'existing_loan_statement'];

// The offers desk keeps its own papers per bank — they belong on the Offers
// Letter tab, not in the customer's document collection.
const OFFER_DOC_TYPES = ['offer_letter', 'bank_offer', 'disbursement_order'];

const slug = (s) => s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 40);

const DocRow = ({ item, leadId, onChanged }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const docs = item.documents || [];

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      await loanProfilingApi.uploadDocument(leadId, item.doc_type, file);
      toast.success(`${item.label} uploaded`);
      await onChanged();
    } catch (err) {
      toast.error(`Upload failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const analyze = async (docId) => {
    setBusy(true);
    try {
      let password = null;
      if (STATEMENT_TYPES.includes(item.doc_type)) {
        password = window.prompt('If the PDF is password-protected, enter the password (else leave blank):') || null;
      }
      const res = await loanProfilingApi.analyzeDocument(leadId, docId, password);
      if (res.data.success) toast.success('AI analysis complete');
      else toast.warning('AI could not fully read this document');
      await onChanged();
    } catch (err) {
      toast.error(`Analyze failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const del = async (docId) => {
    if (!window.confirm('Remove this document?')) return;
    setBusy(true);
    try {
      await loanProfilingApi.deleteDocument(leadId, docId);
      await onChanged();
    } catch (err) {
      toast.error(`Could not remove it: ${err?.response?.data?.detail || err.message}`);
    } finally { setBusy(false); }
  };

  return (
    <div className="p-4 rounded-xl border border-slate-200 bg-white mb-3 group hover:border-blue-300 transition-colors shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 group-hover:text-blue-600 transition-colors">
            {item.uploaded ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <FileText className="w-5 h-5" />}
          </span>
          <div className="font-semibold text-slate-900 text-sm">
            {item.label}
            {item.required
              ? <span className="ml-2 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-rose-50 text-rose-700 border border-rose-200">Required</span>
              : <span className="ml-2 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-slate-50 text-slate-500 border border-slate-200">Optional</span>}
            {docs.length > 1 && (
              <span className="ml-2 text-[11px] font-medium text-slate-400" data-testid={`doc-count-${item.doc_type}`}>
                {docs.length} files
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" hidden onChange={onPick}
            accept=".pdf,.png,.jpg,.jpeg,.webp,.heic" data-testid={`doc-file-input-${item.doc_type}`} />
          <Button size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()}
            className="hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300" data-testid={`doc-upload-btn-${item.doc_type}`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
            {item.uploaded ? 'Add another' : 'Upload'}
          </Button>
        </div>
      </div>

      {/* Every copy on the file, not just the first — duplicates are a real thing on a loan desk. */}
      {docs.map((doc, i) => {
        const status = doc.validation_status || 'PENDING';
        const sfx = i === 0 ? item.doc_type : `${item.doc_type}-${i}`;
        return (
          <div key={doc.id} className="flex items-center justify-between gap-2 mt-3 pl-[52px]">
            <div className="text-xs text-slate-400 truncate max-w-[320px]">{doc.file_name}</div>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLE[status]}`}>
                {status === 'VERIFIED' ? <CheckCircle2 className="w-3 h-3 inline mr-1" /> : <Clock className="w-3 h-3 inline mr-1" />}
                {status}
              </span>
              <a href={loanProfilingApi.fileUrl(leadId, doc.id)} target="_blank" rel="noreferrer"
                className="inline-flex items-center px-2.5 py-1.5 rounded-md border border-slate-200 text-xs hover:bg-slate-50" data-testid={`doc-view-btn-${sfx}`}>
                <Eye className="w-3.5 h-3.5" />
              </a>
              {item.analyzable && (
                <Button size="sm" variant="outline" disabled={busy} onClick={() => analyze(doc.id)}
                  className="hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300" data-testid={`doc-analyze-btn-${sfx}`}>
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />} AI
                </Button>
              )}
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => del(doc.id)} className="text-rose-600 hover:bg-rose-50" data-testid={`doc-delete-btn-${sfx}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Anything already on the file that the checklist doesn't ask for — the KYC and
// statements uploaded from the other tabs, and whatever the rep added himself.
const CollectedRow = ({ doc, leadId, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const del = async () => {
    if (!window.confirm('Remove this document?')) return;
    setBusy(true);
    try {
      await loanProfilingApi.deleteDocument(leadId, doc.id);
      await onChanged();
    } catch (err) {
      toast.error(`Could not remove it: ${err?.response?.data?.detail || err.message}`);
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center justify-between p-3.5 rounded-xl border border-slate-200 bg-white mb-2.5 shadow-sm"
      data-testid={`collected-doc-${doc.id}`}>
      <div className="flex items-center gap-3 min-w-0">
        <span className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
        </span>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900 truncate">{doc.label}</div>
          <div className="text-xs text-slate-400 truncate">{doc.file_name}</div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <a href={loanProfilingApi.fileUrl(leadId, doc.id)} target="_blank" rel="noreferrer"
          className="inline-flex items-center px-2.5 py-1.5 rounded-md border border-slate-200 text-xs hover:bg-slate-50"
          data-testid={`collected-doc-view-${doc.id}`}>
          <Eye className="w-3.5 h-3.5" />
        </a>
        <Button size="sm" variant="ghost" disabled={busy} onClick={del} className="text-rose-600 hover:bg-rose-50"
          data-testid={`collected-doc-delete-${doc.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
};

// A bank often asks for one more paper that no checklist predicts — name it and upload it.
const AddDocument = ({ leadId, onChanged }) => {
  const fileRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const label = name.trim();
      await loanProfilingApi.uploadDocument(leadId, `other_${slug(label) || 'document'}`, file, label);
      toast.success(`${label} uploaded`);
      setName('');
      setOpen(false);
      await onChanged();
    } catch (err) {
      toast.error(`Upload failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="add-document-btn">
        <Plus className="w-3.5 h-3.5 mr-1.5" />Add another document
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-2" data-testid="add-document-form">
      <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="What is it? e.g. Rent agreement"
        className="h-9 w-56 text-sm" data-testid="add-document-name" />
      <input ref={fileRef} type="file" hidden onChange={onPick}
        accept=".pdf,.png,.jpg,.jpeg,.webp,.heic" data-testid="add-document-file" />
      <Button size="sm" disabled={!name.trim() || busy} onClick={() => fileRef.current?.click()}
        className="bg-blue-600 hover:bg-blue-700" data-testid="add-document-upload-btn">
        {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}Upload
      </Button>
      <Button size="sm" variant="ghost" disabled={busy} onClick={() => { setOpen(false); setName(''); }} data-testid="add-document-cancel-btn">
        Cancel
      </Button>
    </div>
  );
};

export const DocumentsTab = ({ leadId, checklist = [], documents = [], onChanged }) => {
  const asked = new Set(checklist.map((c) => c.doc_type));
  const collected = documents.filter((d) => !asked.has(d.doc_type) && !OFFER_DOC_TYPES.includes(d.doc_type));
  const required = checklist.filter((c) => c.required);
  const done = required.filter((c) => c.uploaded).length;

  const groups = {};
  checklist.forEach((c) => { (groups[c.group] = groups[c.group] || []).push(c); });

  return (
    <div data-testid="documents-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            <FolderOpen className="w-4 h-4 text-blue-600" />Documents for this case
          </h4>
          <p className="text-xs text-slate-400 mt-0.5" data-testid="documents-progress">
            {required.length
              ? `${done} of ${required.length} required document${required.length > 1 ? 's' : ''} collected — the list follows how the customer earns and whose name the property is in.`
              : 'Answer how the customer earns to generate the document list.'}
          </p>
        </div>
        <AddDocument leadId={leadId} onChanged={onChanged} />
      </div>

      {collected.length > 0 && (
        <div className="mb-8" data-testid="collected-documents-section">
          <h5 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
            Already collected ({collected.length})
          </h5>
          <div className="grid grid-cols-1 2xl:grid-cols-2 gap-x-3 items-start">
            {collected.map((d) => <CollectedRow key={d.id} doc={d} leadId={leadId} onChanged={onChanged} />)}
          </div>
        </div>
      )}

      {GROUP_ORDER.filter((g) => groups[g]).map((g) => (
        <div key={g} className="mb-7" data-testid={`doc-group-${g}`}>
          <h5 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">{GROUP_LABELS[g] || g}</h5>
          <div className="grid grid-cols-1 2xl:grid-cols-2 gap-x-3 items-start">
            {groups[g].map((item) => (
              <DocRow key={item.doc_type} item={item} leadId={leadId} onChanged={onChanged} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default DocumentsTab;
